/**
 * hundredDisplay — Hundred-format display helpers.
 *
 * The Hundred has no "over" concept — everything is measured in balls.
 * The engine still tracks over_number + ball_in_over internally (5-ball
 * sets are how end-changes and bowler quotas are computed) but every
 * user-facing surface must present balls only.
 *
 * These helpers accept the whole matchType object so the caller can pass
 * whatever it already has (aggregate.match_type, props.matchType, etc.)
 * without having to guess which key holds `code` / `balls_per_over`.
 */

export function isHundred(matchType) {
  if (!matchType) return false;
  const code = (matchType.code || "").toString().toUpperCase();
  return code === "HUNDRED";
}

export function ballsPerOver(matchType) {
  const n = Number(matchType?.balls_per_over);
  return Number.isFinite(n) && n > 0 ? n : 6;
}

/**
 * Innings progress display.
 *   Hundred → "38 balls"
 *   Others  → "6.2"
 * Pass whichever of {legalBalls, completedOvers, ballInOver} you have —
 * the helper reconstructs the rest from balls_per_over.
 */
export function formatInningsProgress(matchType, { legalBalls, completedOvers, ballInOver } = {}) {
  const bpo = ballsPerOver(matchType);
  const totalBalls = legalBalls != null
    ? legalBalls
    : (completedOvers ?? 0) * bpo + (ballInOver ?? 0);
  if (isHundred(matchType)) return `${totalBalls} balls`;
  const ov = completedOvers != null ? completedOvers : Math.floor(totalBalls / bpo);
  const b = ballInOver != null ? ballInOver : totalBalls % bpo;
  return `${ov}.${b}`;
}

/**
 * Bowler quota / spell display.
 *   Hundred → "12 balls"  (max 20)
 *   Others  → "2.0"        (over.ball)
 */
export function formatBowlerBalls(matchType, balls) {
  const bpo = ballsPerOver(matchType);
  const n = Number(balls) || 0;
  if (isHundred(matchType)) return `${n}`;
  return `${Math.floor(n / bpo)}.${n % bpo}`;
}

/**
 * Column header for the bowler stats table.
 *   Hundred → "B"    (balls)
 *   Others  → "O"    (overs)
 */
export function bowlerBallsColumnLabel(matchType) {
  return isHundred(matchType) ? "B" : "O";
}

/**
 * Innings-cap label for chase / DLS chips.
 *   Hundred → "100 balls"
 *   Others  → "20 ov"
 */
export function formatMaxLength(matchType, maxOvers) {
  if (maxOvers == null) return null;
  if (isHundred(matchType)) return `${maxOvers * ballsPerOver(matchType)} balls`;
  return `${maxOvers} ov`;
}

/**
 * Lowest over_number in a set of balls — the value the innings calls its
 * first over.
 *
 * over_number is NOT consistently 1-indexed. Live-scored matches number
 * overs from 1; every INGESTED match numbers them from 0 (5169 innings
 * on prod against 3). Anything that hard-codes one or the other is
 * wrong for the rest, so derive it from the balls in hand.
 *
 * Returns 0 when there is nothing to measure, which is the common case
 * and the safe default.
 */
export function deriveOverBase(balls) {
  let base = null;
  for (const b of balls || []) {
    const ov = Number(b?.over_number ?? b?.overNumber);
    if (Number.isFinite(ov) && (base === null || ov < base)) base = ov;
  }
  return base ?? 0;
}

/**
 * Ball-picker label (edit dialogs, ball-index lists).
 *   Hundred → "Ball 34"
 *   Others  → "3.4"       (overs completed . ball in this over)
 *
 * `overBase` is the innings' first over_number — pass what
 * deriveOverBase() returned for the same set of balls. It defaults to 0
 * because that is how ingested matches are stored.
 *
 * This used to compute `(Number(overNumber) || 1) - 1`, which was wrong
 * twice over on 0-based data: the `|| 1` turned a legitimate over 0 into
 * 1 (0 is falsy), so over 0 AND over 1 both rendered as "0.x", and every
 * later over was shifted down one — leaving the innings' final over with
 * no label at all.
 */
export function formatBallReference(
  matchType,
  { overNumber, ballInOver, absoluteBallNumber, overBase = 0 },
) {
  const raw = Number(overNumber);
  const ov = (Number.isFinite(raw) ? raw : 0) - (Number(overBase) || 0);
  const b = Number(ballInOver) || 0;
  if (isHundred(matchType)) {
    if (absoluteBallNumber != null) return `Ball ${absoluteBallNumber}`;
    return `Ball ${ov * ballsPerOver(matchType) + b}`;
  }
  return `${ov}.${b}`;
}

/**
 * Required-rate math for the ribbon.
 *   Hundred → { need, ballsLeft } — no per-over rate (irrelevant in a
 *             100-ball innings; runs-per-ball is the meaningful figure
 *             and the "Need N from B balls" line already conveys it).
 *   Others  → { need, ballsLeft, rrr } where rrr = need * 6 / ballsLeft.
 */
export function requiredRate(matchType, { need, ballsLeft }) {
  if (!Number.isFinite(need) || !Number.isFinite(ballsLeft) || ballsLeft <= 0) return null;
  if (isHundred(matchType)) return { need, ballsLeft };
  return { need, ballsLeft, rrr: (need * 6) / ballsLeft };
}
